import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

// Roles whose online presence is reported to the backend for widget escalation.
const PRESENCE_ROLES = ['tenant_admin', 'agent'];
const HEARTBEAT_MS = 60000; // 60s — must stay below the backend 2-minute stale window

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('fs_user')); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('fs_token'));
  const heartbeatRef = useRef(null);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem('fs_token', t);
    localStorage.setItem('fs_user', JSON.stringify(u));
    setToken(t);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    // Best-effort: tell the backend we're going offline before clearing auth.
    // Never let a failed presence call block logout.
    try {
      if (PRESENCE_ROLES.includes(user?.role)) {
        await api.post('/auth/presence/offline');
      }
    } catch {
      // ignore — logout must always proceed
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    localStorage.removeItem('fs_token');
    localStorage.removeItem('fs_user');
    setToken(null);
    setUser(null);
  }, [user]);

  // Online-presence heartbeat for tenant staff (tenant_admin / agent) only.
  useEffect(() => {
    const isStaff = !!token && !!user && PRESENCE_ROLES.includes(user?.role);
    if (!isStaff) return undefined;

    let cancelled = false;
    const sendBeat = () => {
      api.post('/auth/presence').catch(() => { /* presence is best-effort */ });
    };

    sendBeat(); // immediate beat on load/login
    heartbeatRef.current = setInterval(() => {
      if (!cancelled) sendBeat();
    }, HEARTBEAT_MS);

    return () => {
      cancelled = true;
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [token, user]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
